import { useState } from 'react';
import { Alert, Button, Text, Stack } from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../../hooks/useAppUser';
import {
  setContactEmail,
  dismissContactEmailNudge,
} from '../../services/contactEmailService';

/**
 * Shown while the account is unreachable: no auth email (Facebook can grant
 * sign-in without one) and no verified contact email. An address that is
 * still awaiting its confirmation link counts as unreachable, so the nudge
 * stays up in a "confirm" variant with a resend action until the link is
 * clicked. Dismissal persists on the profile, like the taste tuner.
 */
export const ContactEmailNudge = () => {
  const { user, profileLoaded } = useAppUser();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // The pending address and dismissal flag ride the backend profile, so
  // deciding before it loads would flash the wrong variant.
  if (
    !user ||
    !profileLoaded ||
    dismissed ||
    user.contactEmailNudgeDismissed ||
    user.email ||
    user.contactEmail
  ) {
    return null;
  }

  const pending = user.pendingContactEmail;

  const handleDismiss = () => {
    // Hide immediately; persisting the flag is best-effort.
    setDismissed(true);
    user
      .getIdToken()
      .then((token) => dismissContactEmailNudge(token))
      .catch(() => {});
  };

  const handleResend = async () => {
    if (!pending) return;
    setResending(true);
    try {
      const token = await user.getIdToken();
      await setContactEmail(pending, token);
      setResent(true);
    } catch {
      // Rate limited or transient; the button stays available to retry.
    } finally {
      setResending(false);
    }
  };

  return (
    <Alert
      color="orange"
      radius="lg"
      icon={<IconMail size={20} />}
      withCloseButton
      closeButtonLabel="Dismiss"
      onClose={handleDismiss}
      title={pending ? 'Confirm your email address' : 'Add an email address'}
    >
      <Stack align="flex-start" gap="sm">
        {pending ? (
          <Text size="sm">
            We sent a confirmation link to <strong>{pending}</strong>. Your
            email isn't active until you click it.
          </Text>
        ) : (
          <Text size="sm">
            Your account has no email on file, so we can't send you your meal
            plans or account updates.
          </Text>
        )}
        {pending ? (
          <Button
            size="xs"
            color="orange"
            onClick={handleResend}
            loading={resending}
            disabled={resent}
          >
            {resent ? 'Link sent!' : 'Resend link'}
          </Button>
        ) : (
          <Button
            size="xs"
            color="orange"
            onClick={() => navigate('/profile?section=contact-email')}
          >
            Add email
          </Button>
        )}
      </Stack>
    </Alert>
  );
};
